import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type {
  StartupActivityTimelineItem as StartupActivityTimelineItemType,
  StartupUserActivityItem,
} from '../../types/startupDashboard';
import clockIcon from '../../assets/icons/clock.png';
import locationIcon from '../../assets/icons/location.png';
import activityChevron from '../../assets/return_flawed_rev.png';
import DashboardSectionTab from './DashboardSectionTab';
import StartupActivityTimelineItem from './StartupActivityTimelineItem';
import { CountryFlag, countryToIso2 } from '../../utils/countryFlag';
import { PrairiScrollbar } from '../profile/PrairiScrollbar';
import { isRevealedTimelineInvestor } from '../../utils/revealedInvestorIdentity';

interface StartupActivityTimelinePanelProps {
  items: StartupActivityTimelineItemType[];
  userActivity?: StartupUserActivityItem[];
  hideHeader?: boolean;
  onSelectInvestor?: (investorId: string, investorName?: string) => void;
}

function formatUserActivityDate(activityDate: Date): string {
  const month = activityDate.toLocaleDateString('en-US', { month: 'short' });
  const day = activityDate.toLocaleDateString('en-US', { day: '2-digit' });
  return `${month} ${day}`;
}

function getLocalDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatTimelineDate(activityDate: Date): { month: string; day: string } {
  const fullMonth = activityDate.toLocaleDateString('en-US', { month: 'long' });
  const shortMonth = activityDate.toLocaleDateString('en-US', { month: 'short' });
  const displayMonth = fullMonth === 'March' ? 'March' : shortMonth;
  const day = activityDate.toLocaleDateString('en-US', { day: '2-digit' });
  return { month: displayMonth, day };
}

function cleanAndSortInvestorItems(
  items: StartupActivityTimelineItemType[]
): StartupActivityTimelineItemType[] {
  const hasConsent = items.some(i => i.eventType === 'favorited_with_consent');
  let filtered = items;
  if (hasConsent) {
    filtered = filtered.filter(i => i.eventType !== 'favorited');
  }

  // Every investor who is following gets a "Viewed pitch deck" row, even if no view was logged.
  const favoriteItem = filtered.find(
    i => i.eventType === 'favorited' || i.eventType === 'favorited_with_consent'
  );
  const hasView = filtered.some(
    i => i.eventType === 'viewed_deck' || i.eventType === 'viewed_profile'
  );
  if (favoriteItem && !hasView) {
    filtered = [
      ...filtered,
      {
        ...favoriteItem,
        id: `${favoriteItem.id}-viewed-deck`,
        eventType: 'viewed_deck',
        text: 'Viewed pitch deck',
      },
    ];
  }

  // Deduplicate views: keep only one viewed_deck or viewed_profile
  let seenView = false;
  filtered = filtered.filter(i => {
    if (i.eventType === 'viewed_deck' || i.eventType === 'viewed_profile') {
      if (seenView) return false;
      seenView = true;
      return true;
    }
    return true;
  });

  const getWeight = (i: StartupActivityTimelineItemType) => {
    if (i.eventType === 'viewed_deck' || i.eventType === 'viewed_profile') return 1;
    if (i.eventType === 'favorited' || i.eventType === 'favorited_with_consent') return 2;
    if (i.eventType === 'feedback_left' || i.text?.toLowerCase().includes('feedback')) return 3;
    if (i.text?.toLowerCase().includes('share')) return 4;
    return 5;
  };

  return [...filtered].sort((a, b) => getWeight(a) - getWeight(b));
}

function activityLocationLabel(investor: {
  city?: string;
  state?: string;
  country: string;
}): string | null {
  const city = investor.city?.trim() || '';
  const state = investor.state?.trim() || '';
  const country = investor.country?.trim() || '';
  const isUs = countryToIso2(country) === 'US';
  if (isUs) {
    if (city && state) return `(${city}, ${state})`;
    if (city) return `(${city})`;
    if (state) return `(${state})`;
    return null;
  }
  if (city) return `(${city})`;
  return null;
}

function TimelineInvestorLabel({
  investor,
  revealed,
  onOpen,
  className,
}: {
  investor: { investorName?: string; country: string; city?: string; state?: string };
  revealed: boolean;
  onOpen: () => void;
  className: string;
}) {
  const label = investor.investorName || `Investor from ${investor.country}`;
  const place = activityLocationLabel(investor);
  const text = (
    <>
      {label}
      {place ? <span className="font-normal text-gray-400"> {place}</span> : null}
    </>
  );
  if (!revealed) {
    return <span className={className}>{text}</span>;
  }
  return (
    <button
      type="button"
      className={`${className} cursor-pointer text-left transition-colors hover:text-[#38bdf8] hover:underline`}
      onClick={onOpen}
    >
      {text}
    </button>
  );
}

function TimelineInvestorAvatar({
  investor,
  revealed,
  size,
  onOpen,
}: {
  investor: { investorName?: string; country: string; profilePictureUrl?: string };
  revealed: boolean;
  size: 'sm' | 'lg';
  onOpen: () => void;
}) {
  const photo = investor.profilePictureUrl?.trim() || '';
  const [photoFailed, setPhotoFailed] = useState(false);
  useEffect(() => {
    setPhotoFailed(false);
  }, [photo]);
  if (photo && !photoFailed) {
    const sizeClass = size === 'sm' ? 'h-6 w-6' : 'h-11 w-11';
    return (
      <img
        src={photo}
        alt={investor.investorName || 'Investor'}
        onClick={revealed ? onOpen : undefined}
        onError={() => setPhotoFailed(true)}
        className={`${sizeClass} shrink-0 rounded-full border border-[#444444] object-cover ${
          revealed ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''
        }`}
        loading="lazy"
      />
    );
  }

  return (
    <CountryFlag
      country={investor.country}
      width={size === 'sm' ? 22 : 42}
      className={size === 'sm' ? 'flex-shrink-0 rounded-sm' : 'rounded-sm shadow-sm'}
    />
  );
}

const StartupActivityTimelinePanel: React.FC<StartupActivityTimelinePanelProps> = ({
  items,
  userActivity = [],
  hideHeader = false,
  onSelectInvestor,
}) => {
  const timelineScrollRef = React.useRef<HTMLDivElement>(null);
  const timelinePanelRef = React.useRef<HTMLDivElement>(null);
  const hasInvestorActivity = items.length > 0;
  const [fetchedPhotos, setFetchedPhotos] = useState<Record<string, string | null>>({});
  const fetchedPhotosRef = useRef(fetchedPhotos);
  fetchedPhotosRef.current = fetchedPhotos;

  const sortedUserActivity = useMemo(() => {
    return [...userActivity]
      .filter(item => {
        const d = new Date(item.timestamp);
        return !Number.isNaN(d.getTime());
      })
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [userActivity]);

  const openRevealedProfile = (investor: { investorId?: string; investorName?: string }) => {
    if (!onSelectInvestor || !isRevealedTimelineInvestor(investor) || !investor.investorId) {
      return;
    }
    onSelectInvestor(investor.investorId, investor.investorName);
  };

  // Group by year → date → investor
  const groupedByYear = useMemo(() => {
    const yearMap = new Map<
      number,
      Map<
        string,
        {
          month: string;
          day: string;
          investors: Map<
            string,
            {
              investorId?: string;
              investorName?: string;
              profilePictureUrl?: string;
              country: string;
              city?: string;
              state?: string;
              items: StartupActivityTimelineItemType[];
            }
          >;
        }
      >
    >();

    items.forEach(item => {
      const activityDate = new Date(item.timestamp);
      if (Number.isNaN(activityDate.getTime())) return;

      const year = activityDate.getFullYear();
      const dateKey = getLocalDateKey(activityDate);
      const { month, day } = formatTimelineDate(activityDate);

      if (!yearMap.has(year)) {
        yearMap.set(year, new Map());
      }
      const dateMap = yearMap.get(year)!;

      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, { month, day, investors: new Map() });
      }
      const dateGroup = dateMap.get(dateKey)!;

      const investorKey = item.investorId
        ? `inv-${item.investorId}`
        : item.investorName
          ? `${item.investorName}-${item.country}`
          : `anonymous-${item.country}-${item.id}`;

      if (!dateGroup.investors.has(investorKey)) {
        dateGroup.investors.set(investorKey, {
          investorId: item.investorId,
          investorName: item.investorName,
          profilePictureUrl: item.profilePictureUrl,
          country: item.country,
          city: item.city,
          state: item.state,
          items: [],
        });
      } else {
        const inv = dateGroup.investors.get(investorKey)!;
        if (!inv.profilePictureUrl && item.profilePictureUrl) {
          inv.profilePictureUrl = item.profilePictureUrl;
        }
        if (!inv.investorId && item.investorId) {
          inv.investorId = item.investorId;
        }
        if (!inv.investorName && item.investorName) {
          inv.investorName = item.investorName;
        }
        if (!inv.city && item.city) inv.city = item.city;
        if (!inv.state && item.state) inv.state = item.state;
      }
      dateGroup.investors.get(investorKey)!.items.push(item);
    });

    return Array.from(yearMap.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([year, dateMap]) => ({
        year,
        dates: Array.from(dateMap.entries())
          .sort((a, b) => b[0].localeCompare(a[0]))
          .map(([dateKey, value]) => ({
            dateKey,
            month: value.month,
            day: value.day,
            investors: Array.from(value.investors.values()).map(inv => {
              const resolvedName =
                inv.investorName || inv.items.find(i => i.investorName)?.investorName;
              const resolvedId = inv.investorId || inv.items.find(i => i.investorId)?.investorId;
              const resolvedPhoto =
                inv.profilePictureUrl ||
                inv.items.find(i => i.profilePictureUrl)?.profilePictureUrl;
              const resolvedCity = inv.city || inv.items.find(i => i.city)?.city;
              const resolvedState = inv.state || inv.items.find(i => i.state)?.state;

              return {
                ...inv,
                investorName: resolvedName,
                investorId: resolvedId,
                profilePictureUrl: resolvedPhoto,
                city: resolvedCity,
                state: resolvedState,
                items: cleanAndSortInvestorItems(inv.items),
              };
            }),
          })),
      }));
  }, [items]);

  const revealedIdsMissingPhoto = useMemo(() => {
    const ids: string[] = [];
    const seen = new Set<string>();
    groupedByYear.forEach(yearGroup => {
      yearGroup.dates.forEach(dateGroup => {
        dateGroup.investors.forEach(investor => {
          const id = investor.investorId?.trim();
          if (!id || seen.has(id)) return;
          if (!isRevealedTimelineInvestor(investor)) return;
          if (investor.profilePictureUrl?.trim()) return;
          seen.add(id);
          ids.push(id);
        });
      });
    });
    return ids;
  }, [groupedByYear]);

  useEffect(() => {
    const pending = revealedIdsMissingPhoto.filter(id => !(id in fetchedPhotosRef.current));
    if (pending.length === 0) return;
    let cancelled = false;
    const getProfile = httpsCallable<
      { userId: string },
      { success?: boolean; profile?: { profilePictureUrl?: string } }
    >(getFunctions(), 'getInvestorProfile');

    pending.forEach(id => {
      getProfile({ userId: id })
        .then(result => {
          if (cancelled) return;
          const url =
            result.data.success && typeof result.data.profile?.profilePictureUrl === 'string'
              ? result.data.profile.profilePictureUrl.trim()
              : '';
          setFetchedPhotos(prev => (id in prev ? prev : { ...prev, [id]: url || null }));
        })
        .catch(() => {
          if (cancelled) return;
          setFetchedPhotos(prev => (id in prev ? prev : { ...prev, [id]: null }));
        });
    });

    return () => {
      cancelled = true;
    };
  }, [revealedIdsMissingPhoto]);

  const photoForInvestor = (investor: {
    investorId?: string;
    profilePictureUrl?: string;
  }): string => {
    const direct = investor.profilePictureUrl?.trim() || '';
    if (direct) return direct;
    const id = investor.investorId?.trim();
    if (!id) return '';
    const fetched = fetchedPhotos[id];
    return typeof fetched === 'string' ? fetched : '';
  };

  const userActivityList =
    sortedUserActivity.length > 0 ? (
      <ul className="space-y-5">
        {sortedUserActivity.map(activity => {
          const activityDate = new Date(activity.timestamp);
          const dateLabel = formatUserActivityDate(activityDate);
          return (
            <li key={activity.id}>
              {/* Mobile — chevron centered under the flag column */}
              <div className="sm:hidden flex items-center gap-8">
                <span className="w-14 shrink-0 text-sm font-normal text-gray-400">{dateLabel}</span>
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center">
                    <img
                      src={activityChevron}
                      alt=""
                      aria-hidden
                      className="h-5 w-5 object-contain"
                    />
                  </span>
                  <span className="text-sm text-white">{activity.text}</span>
                </div>
              </div>
              {/* Desktop — same date column; chevron centered under flags */}
              <div className="hidden sm:grid sm:grid-cols-[minmax(0,6rem)_1fr] sm:gap-16 lg:grid-cols-[minmax(0,7rem)_1fr] lg:gap-20">
                <div className="flex justify-end items-center">
                  <span className="text-sm font-normal text-gray-400">{dateLabel}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center">
                    <img
                      src={activityChevron}
                      alt=""
                      aria-hidden
                      className="h-8 w-8 object-contain"
                    />
                  </span>
                  <span className="text-base text-white">{activity.text}</span>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    ) : null;

  return (
    <section className="h-full text-left">
      {!hideHeader && (
        <div className="mb-6 flex justify-center pt-2">
          <DashboardSectionTab label="Views & Activity" count={items.length} />
        </div>
      )}

      {/* Section title — mobile only */}
      <h3 className="sm:hidden text-sm font-semibold text-white mb-5">Views & Activity</h3>

      {!hasInvestorActivity && (
        <p className="mb-8 text-center text-sm text-gray-400">
          When investors view your profile, deck, or leave feedback, it will show up here.
        </p>
      )}

      {!hasInvestorActivity ? (
        userActivityList
      ) : (
        <div ref={timelinePanelRef} className="relative flex-1 min-h-0">
          <div
            ref={timelineScrollRef}
            className="prairi-hide-native-scrollbar max-h-[calc(100vh-12rem)] overflow-y-auto pr-3 sm:pr-4"
          >
            <div className="space-y-8">
              {groupedByYear.map(({ year, dates }) => (
                <div key={year}>
                  {/* Year header — mobile only */}
                  <p className="sm:hidden text-sm font-bold text-gray-300 mb-4">{year}</p>

                  <div className="space-y-6">
                    {dates.map(({ dateKey, month, day, investors }) => (
                      <div key={dateKey}>
                        {/* ── Mobile: date badge once on left, investors stacked on right ── */}
                        <div className="sm:hidden flex items-start gap-8">
                          {/* Calendar badge */}
                          <div className="flex-shrink-0 flex flex-col items-center justify-center w-10 h-10 rounded-md border border-gray-600 bg-[#1f1f1f] leading-none">
                            <span className="text-[9px] font-semibold text-gray-400 tracking-widest uppercase">
                              {month}
                            </span>
                            <span className="text-base font-bold text-white leading-tight">
                              {day}
                            </span>
                          </div>

                          {/* Investors stacked */}
                          <div className="flex-1 space-y-4 min-w-0">
                            {investors.map((investor, idx) => {
                              const revealed = isRevealedTimelineInvestor(investor);
                              const photo = photoForInvestor(investor);
                              const investorWithPhoto = photo
                                ? { ...investor, profilePictureUrl: photo }
                                : investor;
                              return (
                                <div
                                  key={`mob-${dateKey}-${investor.investorName || 'anon'}-${idx}`}
                                >
                                  <div className="flex items-start gap-2 mb-1.5">
                                    <TimelineInvestorAvatar
                                      investor={investorWithPhoto}
                                      revealed={revealed}
                                      size="sm"
                                      onOpen={() => openRevealedProfile(investor)}
                                    />
                                    <TimelineInvestorLabel
                                      investor={investor}
                                      revealed={revealed}
                                      onOpen={() => openRevealedProfile(investor)}
                                      className="text-sm break-words font-bold text-white"
                                    />
                                  </div>
                                  <ul className="space-y-2 pl-1">
                                    {investor.items.map(activity => (
                                      <li key={activity.id} className="list-none">
                                        <StartupActivityTimelineItem
                                          item={activity}
                                          showHeader={false}
                                          mobileStyle
                                          onSelectInvestor={
                                            revealed
                                              ? () => openRevealedProfile(investor)
                                              : undefined
                                          }
                                        />
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* ── Desktop: clean timeline matching mockup ── */}
                        <div className="hidden sm:grid sm:grid-cols-[minmax(0,6rem)_1fr] sm:gap-16 lg:grid-cols-[minmax(0,7rem)_1fr] lg:gap-20">
                          <div className="flex justify-end pt-1">
                            <span className="text-sm font-normal text-gray-400">
                              {month} {day}
                            </span>
                          </div>
                          <div className="space-y-6">
                            {investors.map((investor, idx) => {
                              const revealed = isRevealedTimelineInvestor(investor);
                              const photo = photoForInvestor(investor);
                              const investorWithPhoto = photo
                                ? { ...investor, profilePictureUrl: photo }
                                : investor;
                              return (
                                <div
                                  key={`desk-${dateKey}-${investor.investorName || 'anon'}-${idx}`}
                                  className="flex items-start gap-4"
                                >
                                  {photo ? (
                                    <TimelineInvestorAvatar
                                      investor={investorWithPhoto}
                                      revealed={revealed}
                                      size="lg"
                                      onOpen={() => openRevealedProfile(investor)}
                                    />
                                  ) : (
                                    <div className="flex h-11 w-11 shrink-0 items-start justify-center">
                                      <div className="-mt-0.5">
                                        <TimelineInvestorAvatar
                                          investor={investorWithPhoto}
                                          revealed={revealed}
                                          size="lg"
                                          onOpen={() => openRevealedProfile(investor)}
                                        />
                                      </div>
                                    </div>
                                  )}
                                  <div className="min-w-0 flex-1 space-y-2 pt-0.5">
                                    <div>
                                      <h4 className="text-base leading-tight block w-full font-bold text-white break-words">
                                        <TimelineInvestorLabel
                                          investor={investor}
                                          revealed={revealed}
                                          onOpen={() => openRevealedProfile(investor)}
                                          className="text-base leading-tight font-bold text-white break-words"
                                        />
                                      </h4>
                                    </div>
                                    <ul className="space-y-2 pt-0.5">
                                      {investor.items.map(activity => (
                                        <li key={activity.id} className="list-none">
                                          <StartupActivityTimelineItem
                                            item={activity}
                                            showHeader={false}
                                            onSelectInvestor={
                                              revealed
                                                ? () => openRevealedProfile(investor)
                                                : undefined
                                            }
                                          />
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {userActivityList ? <div className="pt-6">{userActivityList}</div> : null}
            </div>
          </div>
          <PrairiScrollbar
            scrollRef={timelineScrollRef}
            hoverRootRef={timelinePanelRef}
            thumbHeight={40}
            thumbWidth={6}
            trackInsetY={10}
          />
        </div>
      )}
    </section>
  );
};

export default StartupActivityTimelinePanel;
